/**
 * Markdown Parser using teg-parser combinators
 *
 * This parser uses a hierarchical approach:
 * 1. **Inline parsers** handle text formatting (bold, italic, links) within content
 * 2. **Block parsers** handle structural elements (headings, lists, paragraphs)
 * 3. **Document parser** combines blocks with newline separators
 *
 * The `template` syntax provides clean, readable parser definitions that mirror
 * the actual markdown syntax. For example: `template`**${content}**`` for bold text.
 *
 * Usage:
 * ```ts
 * const doc = parseMarkdown("# Hello\n\nThis is **bold** text");
 * ```
 */

import {
	digit,
	line,
	nOrMore,
	not,
	oneOf,
	oneOrMore,
	Parser,
	takeUpTo,
	template,
	text,
	zeroOrMore,
} from "teg-parser";

// Types
export type MarkdownDoc = {
	type: "doc";
	content: MarkdownNode[];
};

export type MarkdownNode =
	| HeadingNode
	| ParagraphNode
	| BulletListNode
	| OrderedListNode
	| HorizontalDividerNode;

export type InlineNode =
	| TextNode
	| BoldNode
	| ItalicNode
	| LinkNode
	| ImageNode;

export type HeadingNode = {
	type: "heading";
	level: 1 | 2 | 3;
	content: InlineNode[];
};

export type ParagraphNode = {
	type: "paragraph";
	content: InlineNode[];
};

export type BulletListNode = {
	type: "bulletList";
	items: ListItemNode[];
};

export type OrderedListNode = {
	type: "orderedList";
	items: ListItemNode[];
};

export type ListItemNode = {
	type: "listItem";
	content: InlineNode[];
	subItems?: ListItemNode[];
};

export type HorizontalDividerNode = {
	type: "horizontalDivider";
};

export type TextNode = {
	type: "text";
	text: string;
};

export type BoldNode = {
	type: "bold";
	content: InlineNode[];
};

export type ItalicNode = {
	type: "italic";
	content: InlineNode[];
};

export type LinkNode = {
	type: "link";
	href: string;
	content: InlineNode[];
};

export type ImageNode = {
	type: "image";
	src: string;
	alt: string;
};

const boldParser: Parser<BoldNode> = template`**${takeUpTo(text("**"))}**`.map(
	([content]) => ({
		type: "bold",
		content: [{ type: "text", text: content }],
	}),
);

const italicParser: Parser<ItalicNode> = template`*${takeUpTo(text("*"))}*`.map(
	([content]) => ({
		type: "italic",
		content: [{ type: "text", text: content }],
	}),
);

const linkParser: Parser<LinkNode> =
	template`[${takeUpTo(text("]"))}](${takeUpTo(text(")"))})`.map(
		([linkText, href]) => ({
			type: "link",
			href,
			content: [{ type: "text", text: linkText }],
		}),
	);

const imageParser: Parser<ImageNode> =
	template`![${takeUpTo(text("]"))}](${takeUpTo(text(")"))})`.map(
		([alt, src]) => ({
			type: "image",
			src,
			alt,
		}),
	);

// Parse inline content (text with formatting)
const inlineParser: Parser<InlineNode[]> = oneOrMore(
	oneOf([imageParser, boldParser, italicParser, linkParser, not(text("\n"))]),
).map((nodes) => {
	const resolvedNodes: InlineNode[] = [];
	let currentString = "";

	for (const node of nodes) {
		if (typeof node === "string") {
			currentString += node;
		} else {
			if (currentString) {
				resolvedNodes.push({ type: "text", text: currentString });
				currentString = "";
			}
			resolvedNodes.push(node);
		}
	}

	if (currentString) {
		resolvedNodes.push({ type: "text", text: currentString });
	}

	return resolvedNodes;
});

// Block parsers
const headingParser: Parser<HeadingNode> = oneOf([
	template`### ${line}`.map(([content]) => ({
		type: "heading" as const,
		level: 3 as const,
		content: [{ type: "text" as const, text: content }],
	})),
	template`## ${line}`.map(([content]) => ({
		type: "heading" as const,
		level: 2 as const,
		content: [{ type: "text" as const, text: content }],
	})),
	template`# ${line}`.map(([content]) => ({
		type: "heading" as const,
		level: 1 as const,
		content: [{ type: "text" as const, text: content }],
	})),
]);

const horizontalDividerParser: Parser<HorizontalDividerNode> = nOrMore(
	3,
	text("-"),
).map(() => ({ type: "horizontalDivider" as const }));

// Helper type for flat list items with indentation
type FlatListItem = {
	indent: number;
	node: ListItemNode;
	kind: "bullet" | "ordered";
};

// Parse indentation (spaces)
const indent = zeroOrMore(text(" "));

// Bullet and ordered markers with indentation
const bulletMarker = oneOf([text("- "), text("* "), text("+ ")]);
const orderedMarker = template`${oneOrMore(digit)}. `;

const bulletListItemParser: Parser<FlatListItem> =
	template`${indent}${bulletMarker}${inlineParser}`.map(
		([indentSpaces, , content]) => ({
			indent: indentSpaces.length,
			kind: "bullet" as const,
			node: { type: "listItem" as const, content },
		}),
	);

const orderedListItemParser: Parser<FlatListItem> =
	template`${indent}${orderedMarker}${inlineParser}`.map(
		([indentSpaces, , content]) => ({
			indent: indentSpaces.length,
			kind: "ordered" as const,
			node: { type: "listItem" as const, content },
		}),
	);

// Function to nest flat list items into hierarchical structure
function nestListItems(flatItems: FlatListItem[]): ListItemNode[] {
	if (flatItems.length === 0) return [];

	const result: ListItemNode[] = [];
	let i = 0;

	while (i < flatItems.length) {
		const currentItem = flatItems[i];
		if (!currentItem) break;

		const baseIndent = currentItem.indent;

		// Create the current item
		const item: ListItemNode = { ...currentItem.node };

		// Look ahead for sub-items
		let j = i + 1;
		const subItems: FlatListItem[] = [];

		while (j < flatItems.length) {
			const subItem = flatItems[j];
			if (!subItem || subItem.indent <= baseIndent) break;
			subItems.push(subItem);
			j++;
		}

		// If we found sub-items, nest them recursively
		if (subItems.length > 0) {
			item.subItems = nestListItems(subItems);
		}

		result.push(item);
		i = j; // Skip processed sub-items
	}

	return result;
}

const paragraphParser: Parser<ParagraphNode> = inlineParser.map((content) => ({
	type: "paragraph" as const,
	content,
}));

// Combined list parser that handles both bullet and ordered lists together
const anyListItemParser: Parser<FlatListItem> = oneOf([
	bulletListItemParser,
	orderedListItemParser,
]);

const mixedListParser: Parser<BulletListNode | OrderedListNode> = oneOrMore(
	anyListItemParser,
	text("\n"),
).map((flatItems) => {
	const nestedItems = nestListItems(flatItems);
	// Return based on the first item's kind
	const firstKind = flatItems[0]?.kind;
	if (firstKind === "bullet") {
		return {
			type: "bulletList" as const,
			items: nestedItems,
		};
	} else {
		return {
			type: "orderedList" as const,
			items: nestedItems,
		};
	}
});

// Main block parser
const blockParser: Parser<MarkdownNode> = oneOf([
	headingParser,
	horizontalDividerParser,
	mixedListParser,
	paragraphParser,
]);

// Main document parser with flexible blank line handling
const blankLines = zeroOrMore(text("\n"));
const documentParser: Parser<MarkdownDoc> = zeroOrMore(
	blockParser,
	blankLines,
).map((content) => ({
	type: "doc" as const,
	content,
}));

export function parseMarkdown(markdownText: string): MarkdownDoc {
	const result = documentParser.run(markdownText.trim());

	if (result.isSuccess()) {
		return result.value;
	}

	// Fallback to paragraphs if parsing fails
	return {
		type: "doc",
		content: markdownText.split("\n").map(
			(line): ParagraphNode => ({
				type: "paragraph",
				content: [{ type: "text", text: line }],
			}),
		),
	};
}
